
import { Contract } from "appworks/utils/contracts/contract";
import { Sequence } from "appworks/utils/contracts/sequence";
import { ThemeBaseAnimateSymbolBehaviour } from "behaviours/theme-base-animate-symbol-behaviour";
import { SymbolSubcomponent } from "slotworks/components/matrix/symbol/symbol-subcomponent";

/**
 * ThemeAnimateSymbolBehaviour is a class that extends ThemeBaseAnimateSymbolBehaviour. We made changes in this class in order to support scaling
 * symbol animations as well as a change to the animate override that allows for animations to be played and loop a number of times dictated by a
 * new parameter called 'chain'
 */
export class ThemeAnimateSymbolBehaviour extends ThemeBaseAnimateSymbolBehaviour {
    public behaviourName: string = "Animate";

    /**
     * Same as AnimateOverBehaviour except the static is hidden during the animation
     *
     * @param symbol {SymbolSubcomponent}
     * @param [defaultAniamtion="win"] {string}
     */
    constructor(symbol: SymbolSubcomponent, defaultAnimation: string = "win", protected alwaysOnTop: boolean = false) {
        super(symbol, defaultAnimation);
    }

    public override static() {
        if (this.animation) {
            this.symbol.setVisible(true);
        }

        super.static();
    }

    public override highlight(): Contract<void> {
        return this.animate(["_highlight", "_win"], [this.defaultAnimation], false, true);
    }

    public override win(): Contract<void> {
        return Contract.empty();
    }

    public override cycle(): Contract<void> {
        return this.animate("_win", [this.defaultAnimation]);
    }

    /**
     * We added a new 'chain' parameter to allow for certain animations to loop for a predetermined number of times. Symbol visibility can be toggled
     * inside of this override to support scaling symbol animations as well.
     *
     * @param chain - This parameter dictates how many times the animation will fire off during per animate call. This parameter defaults to 1.
     */
    public override animate(animSuffix: string | string[], defaultAnimations?: string[], autoReset: boolean = false, loop: boolean = false, chain: number = 1) {
        this.resolveUnfinishedAnimation();
        this.symbol.setVisible(false);
        const sequence: Array<() => Contract<void>> = [];
        for (let i = 0; i < chain; i++) {
            sequence.push(() => new Contract<void>((resolve) => {
                super.animate(animSuffix, defaultAnimations, autoReset, loop).then(() => {
                    if (!loop) {
                        this.symbol.setVisible(true);
                    }
                    resolve(null);
                });
            })
            );
        }

        return new Sequence(sequence);
    }

    public override updateTransform(): void {
        super.updateTransform();

        if (this.alwaysOnTop) {
            this.animation?.parent?.addChildAt(this.animation, this.animation?.parent?.children?.length ?? 0);
        }
    }
}
